import { action } from "mobx";
import { observer } from "mobx-react-lite";
import { styled } from "@mui/material/styles";
import React, { Children, FunctionComponent, PropsWithChildren, ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { mergeRefs } from "react-merge-refs";
import { useDndOrderedStore } from "designSystem/dnd/index";
import { useIntersectionObserver, useResizeObserver } from "designSystem/hooks";

// TODO: Render More button if not infinite scrolling

export type CssGridColumns =
	| 1
	| 2
	| 3
	| 4
	| 6
	| 12;

export type CssGridColumnByWidth = [CssGridColumns, number];
export type CssGridDropFunction = (dragIndex: number, dropIndex: number) => void;

function getColumnsFromWidth(columnsByWidth: CssGridColumnByWidth[], width: number): number {
	return columnsByWidth.reduce((memo, value) => {
		const [columns, threshold] = value;
		return (width >= threshold) ? columns : memo;
	}, 0);
}

const PREFIX = "CSSGrid";

const classes = {
	firefoxTruncFix: `${PREFIX}FirefoxTruncFix`,
};

const Root = styled("div")(({
	theme,
}) => ({
	display: "grid",
	gridTemplateColumns: "repeat(12, 1fr)",

	["& .columns1"]: {
		gridColumnEnd: "span 12",
	},
	["& .columns2"]: {
		gridColumnEnd: "span 6",
	},
	["& .columns3"]: {
		gridColumnEnd: "span 4",
	},
	["& .columns4"]: {
		gridColumnEnd: "span 3",
	},
	["& .columns6"]: {
		gridColumnEnd: "span 2",
	},
	["& .columns12"]: {
		gridColumnEnd: "span 1",
	},
	["&.spacing0"]: {
		gridGap: 0,
	},
	["&.spacing1"]: {
		gridGap: theme.spacing(1),
	},
	["&.spacing2"]: {
		gridGap: theme.spacing(2),
	},
	["&.spacing3"]: {
		gridGap: theme.spacing(3),
	},
	["&.spacing4"]: {
		gridGap: theme.spacing(4),
	},
	// Setting min-width=0 is workaround for firefox bug, when text truncation will not work with flex on ff
	// https://stackoverflow.com/questions/34199345/text-overflow-ellipsis-and-flex-not-working-on-firefox
	[`& .${classes.firefoxTruncFix}`]: {
		minWidth: 0,
	},
}));

export interface CssGridProps extends PropsWithChildren {
	/** catch the all appearances of the intersection ref */
	catchAllAppearances?: boolean;

	/** CSS class */
	className?: string;

	/** fixed number of columns to render per row based on 12 column layout (defaults to 1 if not specified, nor columnsByWidth) */
	columns?: CssGridColumns;

	/** dynamic number of columns to render based on container width */
	columnsByWidth?: CssGridColumnByWidth[];

	/** data-csi-automation value */
	dataCsiAutomation?: string;

	/** indicates if grid allows drag and drop of its items as prescribed by store in context (DndOrderedStore) */
	dnd?: boolean;

	/** indicates there are more results to show in the grid */
	hasMoreResults?: boolean;

	/** indicates more results should be loaded automatically (as opposed to being triggered manually by button click) */
	infiniteScrolling?: boolean;

	/** number of rows before the last where to detect infinite scrolling (default is to place on last row) */
	infiniteScrollRows?: number;

	/** callback to load more results */
	onMoreResults?: () => void;

	/** callback called to indicate number of columns being rendered */
	onNumberOfColumns?: (columns: number) => void;

	/** indicate that the scroll direction to load more is up */
	scrollDirectionIsUp?: boolean;

	/** spacing between grid items (grid-gap) */
	spacing?:
	| 1
	| 2
	| 3
	| 4;
}

interface CssGridItemProps extends Pick<CssGridProps, "className" | "onMoreResults" | "children"> {
	/** catch the all appearances of the intersection ref */
	catchAllAppearances?: boolean;

	/** ref object to track intersection with viewport to load more results */
	intersectionRef?: RefObject<HTMLDivElement>;

	/** handler of onclick */
	onClick?: VoidFunction;
};

const CssGridItem: FunctionComponent<CssGridItemProps> = (props) => {
	const { children, className, intersectionRef, onClick } = props;

	return (
		<div
			className={className}
			onClick={onClick}
			ref={intersectionRef}
		>
			{children}
		</div>
	);
};

const CssGridItemIntersectionObserver: FunctionComponent<CssGridItemProps> = (props) => {
	const { catchAllAppearances, onMoreResults, ...otherProps } = props;
	const ref = useRef<HTMLDivElement>(null);

	useIntersectionObserver(ref, {
		freezeOnceVisible: !catchAllAppearances,
		onIntersection: onMoreResults,
	});

	return (
		<CssGridItem intersectionRef={ref} {...otherProps} />
	);
};

const CssGrid: FunctionComponent<CssGridProps> = observer((props) => {
	const {
		children,
		className,
		columns,
		columnsByWidth,
		dataCsiAutomation,
		dnd,
		hasMoreResults,
		infiniteScrolling,
		infiniteScrollRows,
		catchAllAppearances,
		onMoreResults,
		onNumberOfColumns,
		scrollDirectionIsUp,
		spacing,
	} = props;

	const [width, setWidth] = useState(0);
	const actualChildren = Children.toArray(children);
	const { length } = actualChildren;
	const getNumberOfColumns = (width: number) => columns || (columnsByWidth && width ? getColumnsFromWidth(columnsByWidth, width) : 0);

	// determine number of columns per row and appropriate CSS
	const resolvedColumns = getNumberOfColumns(width);
	const columnsClassName = `columns${resolvedColumns}`;
	const childClassName = `${classes.firefoxTruncFix} ${columnsClassName}`;
	const spacingClassName = `spacing${spacing || 0}` || "";
	const containerClassName = `${spacingClassName} ${className || ""}`;

	const csiAutomation = dataCsiAutomation ? { "data-csi-automation" : dataCsiAutomation } : {};

	// if infinite scrolling, determine index at which need to detect infinite scroll
	let detectInfiniteScrollIndex = -1;

	if (length && hasMoreResults && infiniteScrolling && onMoreResults) {
		detectInfiniteScrollIndex = scrollDirectionIsUp ? 0 : length - 1;

		if (infiniteScrollRows) {
			// put infinite scrolling detection on item that is 1 or more rows up
			const backupCount = infiniteScrollRows * resolvedColumns;
			if (backupCount < length) {
				detectInfiniteScrollIndex -= backupCount;
			}
		}
	}

	// detect change to width of grid in order to determine the number of columns to display
	const { ref } = useResizeObserver<HTMLDivElement>({
		onResize: action(({ width }) => {
			onNumberOfColumns?.(getNumberOfColumns(width));
			setWidth(width);
		}),
	});

	// support for DnD
	const dndStore = dnd ? useDndOrderedStore() : null;
	const [, drop] = dndStore ? useDrop(() => ({ accept: dndStore.dndType })) : [null, null];

	useEffect(() => {
		dndStore?.setSize(length);
	}, [length]);

	// render children
	const gridItems: ReactNode[] = [];

	if (childClassName) {
		for (let i = 0; i < length; ++i) {
			const originalIndex = dndStore?.getOriginalIndex(i) ?? i;
			const ItemComponent = (i === detectInfiniteScrollIndex) ? CssGridItemIntersectionObserver : CssGridItem;

			const props = actualChildren[originalIndex]["props"];
			const _onClick = props.onClick;
			const className = `${childClassName} ${props.className || ""}`;

			gridItems.push(<ItemComponent
				catchAllAppearances={catchAllAppearances}
				className={className}
				key={originalIndex}
				onClick={_onClick}
				onMoreResults={onMoreResults}
			               >
				{actualChildren[originalIndex]}
			</ItemComponent>);
		}
	}

	return (
		<Root 
			className={containerClassName} 
			ref={mergeRefs([ref, drop])} 
			{...csiAutomation}
		>
			{gridItems}
		</Root>
	);
});

export default CssGrid;